if (!customElements.get('course-waitlist-page')) {
  class CourseWaitlistPage extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.initializationScheduled) return;

      this.initializationScheduled = true;
      this.initializationFrame = window.requestAnimationFrame(() => {
        this.initializationFrame = null;
        this.initializationScheduled = false;

        if (!this.isConnected || this.initialized) return;

        this.initialize();
      });
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;

      this.initialized = true;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reveals = Array.from(this.querySelectorAll('[data-waitlist-reveal]'));
      this.galleryViewport = this.querySelector('[data-waitlist-gallery-viewport]');
      this.galleryDots = Array.from(this.querySelectorAll('[data-waitlist-gallery-dot]'));
      this.galleryPrevious = this.querySelector('[data-waitlist-gallery-previous]');
      this.galleryNext = this.querySelector('[data-waitlist-gallery-next]');
      this.video = this.querySelector('.course-waitlist__video');
      this.videoPlayButton = this.querySelector('[data-waitlist-video-play]');
      this.abortController = new AbortController();
      this.revealObserver = null;
      this.videoObserver = null;
      this.galleryFrame = null;
      this.galleryResizeFrame = null;
      this.heroRevealFrame = null;
      this.galleryResizeObserver = null;
      this.galleryActiveIndex = 0;
      this.videoIsVisible = false;
      this.videoPlayPending = false;

      this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
      this.handleGalleryScroll = this.handleGalleryScroll.bind(this);
      this.handleGalleryKeydown = this.handleGalleryKeydown.bind(this);
      this.handleGalleryResize = this.handleGalleryResize.bind(this);
      this.handleFormLinkClick = this.handleFormLinkClick.bind(this);
      this.handleVideoPlayClick = this.handleVideoPlayClick.bind(this);
      this.handleVideoPlaybackState = this.handleVideoPlaybackState.bind(this);

      this.initializeReveals();
      this.initializeGallery();
      this.initializeVideo();
      this.initializeFormLink();

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this.handleReducedMotionChange, {
          signal: this.abortController.signal,
        });
      } else {
        this.reducedMotion.addListener(this.handleReducedMotionChange);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.revealObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.galleryResizeObserver?.disconnect();
      this.video?.pause();

      if (this.initializationFrame !== null && this.initializationFrame !== undefined) {
        window.cancelAnimationFrame(this.initializationFrame);
      }

      if (this.galleryFrame !== null && this.galleryFrame !== undefined) {
        window.cancelAnimationFrame(this.galleryFrame);
      }

      if (this.galleryResizeFrame !== null && this.galleryResizeFrame !== undefined) {
        window.cancelAnimationFrame(this.galleryResizeFrame);
      }

      if (this.heroRevealFrame !== null && this.heroRevealFrame !== undefined) {
        window.cancelAnimationFrame(this.heroRevealFrame);
      }

      if (this.reducedMotion && typeof this.reducedMotion.removeEventListener !== 'function') {
        this.reducedMotion.removeListener(this.handleReducedMotionChange);
      }

      delete this.dataset.enhanced;
      this.reveals?.forEach((element) => {
        element.removeAttribute('data-reveal-ready');
        element.removeAttribute('data-revealed');
      });

      this.initialized = false;
      this.initializationScheduled = false;
      this.initializationFrame = null;
      this.galleryFrame = null;
      this.galleryResizeFrame = null;
      this.heroRevealFrame = null;
      this.revealObserver = null;
      this.videoObserver = null;
      this.galleryResizeObserver = null;
      this.galleryActiveIndex = 0;
    }

    initializeReveals() {
      if (this.reducedMotion.matches || !('IntersectionObserver' in window) || this.reveals.length === 0) {
        this.revealAll();
        return;
      }

      let observer;

      try {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.dataset.revealed = 'true';
              observer.unobserve(entry.target);
            });
          },
          { threshold: 0.16 }
        );

        this.reveals.forEach((element) => {
          element.dataset.revealReady = 'true';

          if (element.hasAttribute('data-waitlist-hero')) {
            this.heroRevealFrame = window.requestAnimationFrame(() => {
              this.heroRevealFrame = null;

              if (this.isConnected) {
                element.dataset.revealed = 'true';
              }
            });
          } else {
            observer.observe(element);
          }
        });

        this.revealObserver = observer;
        this.dataset.enhanced = 'true';
      } catch (error) {
        observer?.disconnect();
        this.revealAll();
      }
    }

    revealAll() {
      if (this.heroRevealFrame) {
        window.cancelAnimationFrame(this.heroRevealFrame);
        this.heroRevealFrame = null;
      }

      delete this.dataset.enhanced;
      this.reveals.forEach((element) => {
        element.removeAttribute('data-reveal-ready');
        element.dataset.revealed = 'true';
      });
    }

    initializeGallery() {
      if (!this.galleryViewport || this.galleryDots.length === 0) return;

      this.galleryViewport.addEventListener('scroll', this.handleGalleryScroll, {
        passive: true,
        signal: this.abortController.signal,
      });
      this.galleryViewport.addEventListener('keydown', this.handleGalleryKeydown, {
        signal: this.abortController.signal,
      });

      if (typeof ResizeObserver === 'function') {
        this.galleryResizeObserver = new ResizeObserver(this.handleGalleryResize);
        this.galleryResizeObserver.observe(this.galleryViewport);
      } else {
        window.addEventListener('resize', this.handleGalleryResize, {
          signal: this.abortController.signal,
        });
      }

      this.galleryDots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => {
            this.goToGalleryIndex(this.galleryDots.indexOf(dot));
          },
          { signal: this.abortController.signal }
        );
      });

      this.galleryPrevious?.addEventListener(
        'click',
        () => this.goToGalleryIndex(this.currentGalleryIndex() - 1),
        { signal: this.abortController.signal }
      );
      this.galleryNext?.addEventListener(
        'click',
        () => this.goToGalleryIndex(this.currentGalleryIndex() + 1),
        { signal: this.abortController.signal }
      );

      this.updateGalleryControls();
    }

    currentGalleryIndex() {
      if (!this.galleryViewport || this.galleryViewport.clientWidth === 0) return 0;
      return Math.max(
        0,
        Math.min(
          Math.round(this.galleryViewport.scrollLeft / this.galleryViewport.clientWidth),
          this.galleryDots.length - 1
        )
      );
    }

    handleGalleryScroll() {
      this.galleryActiveIndex = this.currentGalleryIndex();

      if (this.galleryFrame) return;

      this.galleryFrame = window.requestAnimationFrame(() => {
        this.galleryFrame = null;
        this.updateGalleryControls();
      });
    }

    handleGalleryResize() {
      if (!this.galleryViewport || this.galleryResizeFrame) return;

      this.galleryResizeFrame = window.requestAnimationFrame(() => {
        this.galleryResizeFrame = null;

        if (!this.galleryViewport || this.galleryViewport.clientWidth === 0) return;

        const activeIndex = Math.max(0, Math.min(this.galleryActiveIndex, this.galleryDots.length - 1));
        this.galleryViewport.scrollTo({
          left: activeIndex * this.galleryViewport.clientWidth,
          behavior: 'auto',
        });
        this.updateGalleryControls(activeIndex);
      });
    }

    handleGalleryKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.goToGalleryIndex(this.currentGalleryIndex() + (event.key === 'ArrowRight' ? 1 : -1));
    }

    goToGalleryIndex(index) {
      if (!this.galleryViewport) return;

      const nextIndex = Math.max(0, Math.min(index, this.galleryDots.length - 1));
      this.galleryActiveIndex = nextIndex;
      this.galleryViewport.scrollTo({
        left: nextIndex * this.galleryViewport.clientWidth,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    updateGalleryControls(index = this.currentGalleryIndex()) {
      this.galleryActiveIndex = index;

      this.galleryDots.forEach((dot, dotIndex) => {
        if (dotIndex === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.galleryPrevious) this.galleryPrevious.disabled = index === 0;
      if (this.galleryNext) this.galleryNext.disabled = index >= this.galleryDots.length - 1;
    }

    initializeVideo() {
      if (!this.video) return;

      this.ensureInlineVideo();
      this.updateVideoPlaybackUI();

      this.videoPlayButton?.addEventListener('click', this.handleVideoPlayClick, {
        signal: this.abortController.signal,
      });

      ['play', 'playing', 'pause', 'ended', 'error'].forEach((eventName) => {
        this.video.addEventListener(eventName, this.handleVideoPlaybackState, {
          signal: this.abortController.signal,
        });
      });

      if (this.reducedMotion.matches || !('IntersectionObserver' in window) || this.videoObserver) return;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.videoIsVisible = entry.isIntersecting && entry.intersectionRatio >= 0.35;

            if (!this.videoIsVisible) {
              this.video.pause();
              this.updateVideoPlaybackUI();
              return;
            }

            this.attemptVideoPlayback();
          });
        },
        { threshold: [0, 0.35] }
      );
      this.videoObserver.observe(this.video);
    }

    ensureInlineVideo() {
      if (!this.video) return;

      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.loop = true;
      this.video.controls = false;
      this.video.playsInline = true;
      this.video.setAttribute('muted', '');
      this.video.setAttribute('loop', '');
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('webkit-playsinline', '');
      this.video.removeAttribute('controls');
    }

    updateVideoPlaybackUI() {
      if (!this.videoPlayButton || !this.video) return;

      this.videoPlayButton.hidden = !this.video.paused && !this.video.ended;
    }

    handleVideoPlaybackState() {
      this.updateVideoPlaybackUI();
    }

    handleVideoPlayClick() {
      if (!this.video) return;

      this.ensureInlineVideo();
      this.videoPlayPending = true;
      let playback;

      try {
        playback = this.video.play();
      } catch (error) {
        this.video.pause();
        this.videoPlayPending = false;
        this.updateVideoPlaybackUI();
        return;
      }

      if (!playback || typeof playback.then !== 'function') {
        this.videoPlayPending = false;
        this.updateVideoPlaybackUI();
        return;
      }

      playback
        .catch(() => {
          this.updateVideoPlaybackUI();
        })
        .finally(() => {
          this.videoPlayPending = false;
          this.updateVideoPlaybackUI();
        });
    }

    attemptVideoPlayback() {
      if (!this.video || this.videoPlayPending) return;

      if (this.reducedMotion.matches || !this.videoIsVisible) {
        this.video.pause();
        this.updateVideoPlaybackUI();
        return;
      }

      this.ensureInlineVideo();
      this.videoPlayPending = true;
      let playback;

      try {
        playback = this.video.play();
      } catch (error) {
        this.video.pause();
        this.videoPlayPending = false;
        this.updateVideoPlaybackUI();
        return;
      }

      if (!playback || typeof playback.then !== 'function') {
        this.videoPlayPending = false;
        this.updateVideoPlaybackUI();
        return;
      }

      playback
        .catch(() => {
          this.updateVideoPlaybackUI();
        })
        .finally(() => {
          this.videoPlayPending = false;
          this.updateVideoPlaybackUI();
        });
    }

    initializeFormLink() {
      const formLink = this.querySelector('[data-waitlist-form-link]');
      if (!formLink) return;

      formLink.addEventListener('click', this.handleFormLinkClick, {
        signal: this.abortController.signal,
      });
    }

    handleFormLinkClick(event) {
      const formArea = this.querySelector('#course-waitlist-form');
      const emailInput = formArea?.querySelector('input[name="contact[email]"]');
      if (!formArea) return;

      event.preventDefault();

      if (this.reducedMotion.matches) {
        formArea.scrollIntoView({ behavior: 'auto', block: 'center' });

        if (emailInput) {
          window.requestAnimationFrame(() => {
            emailInput.focus({ preventScroll: true });
          });
        }

        return;
      }

      formArea.scrollIntoView({ behavior: 'smooth', block: 'center' });

      if (emailInput) {
        window.setTimeout(() => {
          emailInput.focus({ preventScroll: true });
        }, 450);
      }
    }

    handleReducedMotionChange() {
      if (this.reducedMotion.matches) {
        this.revealObserver?.disconnect();
        this.revealAll();
        this.video?.pause();
        this.updateVideoPlaybackUI();
        return;
      }

      if (!this.videoObserver) {
        this.initializeVideo();
      } else if (this.videoIsVisible) {
        this.attemptVideoPlayback();
      }
    }
  }

  customElements.define('course-waitlist-page', CourseWaitlistPage);
}
